// BeyPongRefereePad — Iter 40. Referee UI for the BEY-PONG scoring format.
// Owns the full BEY-PONG state machine transitions: 4→3 bey selection,
// hidden-order lock/reveal (referee-only visibility until both locked),
// per-round finish scoring with Burst priority, launch-error counter,
// draw, undo, set/match progression. Replaces RefereeScoringPad when the
// tournament's scoring_format === "bey_pong".
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { EyeSlash, Eye, Lock, LockOpen, ArrowUUpLeft, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { t } from "../../lib/i18n";

const FINISH_BTNS = [
  { key: "spin",   label: "spin",   pts: 1, accent: "#00E5FF" },
  { key: "over",   label: "over",   pts: 2, accent: "#00E5FF" },
  { key: "burst",  label: "burst",  pts: 2, accent: "#FF0055" },
  { key: "tybuch", label: "tybuch", pts: 2, accent: "#9B7AFF" },
  { key: "xtreme", label: "xtreme", pts: 3, accent: "#E2FF00" },
  { key: "zetek",  label: "zetek",  pts: 0, accent: "#FF9F1C" },
];

export default function BeyPongRefereePad({ selected, playerA, playerB, componentsById, deckA, deckB, reload }) {
  const [busy, setBusy] = useState(false);
  const [burstPriority, setBurstPriority] = useState(false);
  // Referee-only "peek" toggles for the hidden orders. Never persisted.
  const [peekA, setPeekA] = useState(false);
  const [peekB, setPeekB] = useState(false);

  const state = selected.bey_pong_state || null;
  const beysA = useMemo(() => (deckA?.beys || []).map((b) => ({
    id: b.blade || b.over_blade || `bA-${Math.random()}`,
    label: nameFor(componentsById, b),
    parts: b,
  })), [deckA, componentsById]);
  const beysB = useMemo(() => (deckB?.beys || []).map((b) => ({
    id: b.blade || b.over_blade || `bB-${Math.random()}`,
    label: nameFor(componentsById, b),
    parts: b,
  })), [deckB, componentsById]);

  const doApi = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${selected.id}${path}`, body || {});
      await reload();
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <div className="text-white/50 text-sm p-6 font-mono" data-testid="beypong-empty">
        BEY-PONG state initializing... make a move to start the match.
        <button
          disabled={busy}
          onClick={() => doApi("/beypong/select-three", { player_id: selected.player_a_id, bey_ids: (beysA.slice(0, 3).map((x) => x.id)) })}
          className="mt-3 block px-3 py-2 bg-[#FF0055] text-white text-xs uppercase"
          data-testid="beypong-bootstrap"
        >
          Bootstrap state
        </button>
      </div>
    );
  }

  const phase = state.phase;
  const revealed = !!state.order_revealed;

  return (
    <div data-testid="beypong-pad" className="border border-[#E2FF00]/40 bg-[#0C0C14] p-5 space-y-5">
      <Header
        selected={selected}
        state={state}
        playerA={playerA}
        playerB={playerB}
      />

      {phase === "select_beys" && (
        <SelectBeys
          state={state}
          allA={beysA}
          allB={beysB}
          selected={selected}
          doApi={doApi}
          busy={busy}
        />
      )}

      {phase === "lock_order" && (
        <LockOrder
          state={state}
          selected={selected}
          allA={beysA}
          allB={beysB}
          peekA={peekA}
          peekB={peekB}
          setPeekA={setPeekA}
          setPeekB={setPeekB}
          doApi={doApi}
          busy={busy}
        />
      )}

      {phase === "set_end" && (
        <SetEnd
          state={state}
          selected={selected}
          doApi={doApi}
          busy={busy}
        />
      )}

      {phase === "live" && (
        <LiveRound
          state={state}
          selected={selected}
          componentsById={componentsById}
          allA={beysA}
          allB={beysB}
          burstPriority={burstPriority}
          setBurstPriority={setBurstPriority}
          doApi={doApi}
          busy={busy}
        />
      )}

      {phase === "complete" && (
        <div
          data-testid="beypong-match-end"
          className="border border-[#E2FF00]/50 bg-[#E2FF00]/10 p-4 text-center"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E2FF00]">
            {t.beyPongMatchEnd}
          </div>
          <div className="mt-2 font-display font-black uppercase italic text-2xl">
            {state.set_wins_a > state.set_wins_b ? (playerA?.handle || "A") : (playerB?.handle || "B")} — {state.set_wins_a}:{state.set_wins_b}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          disabled={busy || (state.history || []).length === 0}
          onClick={() => doApi("/beypong/undo")}
          data-testid="beypong-undo"
          className="px-3 py-2 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-xs uppercase tracking-[0.25em] font-bold disabled:opacity-30 inline-flex items-center gap-2"
        >
          <ArrowUUpLeft size={12} /> Undo
        </button>
        <span className="text-[10px] font-mono text-white/40 self-center">
          {(state.history || []).length} events
        </span>
      </div>
    </div>
  );
}

function Header({ selected, state, playerA, playerB }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-white/50">
        <span>BEY-PONG · {t.beyPongCurrentSet} {state.set_index + 1} · {t.beyPongCurrentCycle} {state.cycle_index + 1} · {t.beyPongCurrentRound} {state.round_in_cycle + 1}/3</span>
        <span className="text-[#E2FF00]">BO{state.sets_to_win === 3 ? 5 : 3}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <SideBadge accent="#00E5FF" name={playerA?.handle || "A"} setScore={state.set_score_a} setWins={state.set_wins_a} totalPts={state.total_points_a} side="a" launchErr={state.launch_err_a} />
        <SideBadge accent="#FF0055" name={playerB?.handle || "B"} setScore={state.set_score_b} setWins={state.set_wins_b} totalPts={state.total_points_b} side="b" launchErr={state.launch_err_b} />
      </div>
    </div>
  );
}

function SideBadge({ accent, name, setScore, setWins, totalPts, side, launchErr }) {
  return (
    <div data-testid={`beypong-badge-${side}`} className="border p-3" style={{ borderColor: `${accent}55` }}>
      <div className="font-display font-black italic uppercase text-lg truncate">{name}</div>
      <div className="flex items-end gap-3 mt-1">
        <span className="font-display font-black italic tabular-nums text-4xl" style={{ color: accent }} data-testid={`beypong-setscore-${side}`}>{setScore}</span>
        <div className="text-[10px] font-mono uppercase text-white/50">
          <div>{t.beyPongSetWins}: <span className="text-white">{setWins}</span></div>
          <div>{t.beyPongTotalPoints}: <span className="text-white">{totalPts}</span></div>
        </div>
      </div>
      {launchErr > 0 && (
        <div className="text-[10px] font-mono uppercase text-[#FF9F1C] mt-1">
          launch err: {launchErr} (next err → opponent +1)
        </div>
      )}
    </div>
  );
}

function SelectBeys({ state, allA, allB, selected, doApi, busy }) {
  return (
    <div className="space-y-3" data-testid="beypong-phase-select">
      <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-white/60">
        {t.beyPongSelectThree}
      </div>
      <ThreeOfFour
        label={selected.player_a_id ? "A" : ""}
        options={allA}
        selectedIds={state.selected_a}
        onCommit={(ids) => doApi("/beypong/select-three", { player_id: selected.player_a_id, bey_ids: ids })}
        busy={busy}
        side="a"
      />
      <ThreeOfFour
        label="B"
        options={allB}
        selectedIds={state.selected_b}
        onCommit={(ids) => doApi("/beypong/select-three", { player_id: selected.player_b_id, bey_ids: ids })}
        busy={busy}
        side="b"
      />
    </div>
  );
}

function ThreeOfFour({ label, options, selectedIds, onCommit, busy, side }) {
  const [picks, setPicks] = useState(selectedIds || []);
  const toggle = (id) => setPicks((p) => (p.includes(id) ? p.filter((x) => x !== id) : (p.length < 3 ? [...p, id] : p)));
  if (options.length < 4) {
    return (
      <div className="text-[11px] font-mono text-[#FF0055]" data-testid={`beypong-select-${side}-nodeck`}>
        Player {label} has fewer than 4 registered Beys — cannot select. Ask the player to register a BEY-PONG deck (4 Beys) and rejoin.
      </div>
    );
  }
  return (
    <div className="border border-white/10 p-3" data-testid={`beypong-select-${side}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 mb-2">Player {label} — pick 3 of 4</div>
      <div className="grid grid-cols-2 gap-1.5">
        {options.map((o) => {
          const on = picks.includes(o.id);
          return (
            <button
              key={o.id}
              disabled={busy}
              onClick={() => toggle(o.id)}
              data-testid={`beypong-pick-${side}-${o.id}`}
              className={`text-left px-2 py-1.5 text-[11px] border font-mono ${on ? "border-[#E2FF00] bg-[#E2FF00]/10 text-[#E2FF00]" : "border-white/15 text-white/70 hover:border-white/30"}`}
            >
              {on ? "✓ " : ""}{o.label}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] font-mono text-white/40">{picks.length}/3 selected</span>
        <button
          disabled={busy || picks.length !== 3}
          onClick={() => onCommit(picks)}
          data-testid={`beypong-commit-${side}`}
          className="px-3 py-1.5 bg-[#FF0055] text-white text-[10px] uppercase font-bold tracking-[0.25em] disabled:opacity-40"
        >
          Commit
        </button>
      </div>
    </div>
  );
}

function LockOrder({ state, selected, allA, allB, peekA, peekB, setPeekA, setPeekB, doApi, busy }) {
  return (
    <div className="space-y-3" data-testid="beypong-phase-order">
      <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-white/60">
        {t.beyPongHiddenOrder}
      </div>
      <OrderPicker
        label="A"
        side="a"
        allBeys={allA}
        selectedIds={state.selected_a}
        alreadyLocked={state.order_a_locked}
        peek={peekA}
        setPeek={setPeekA}
        lockedOrder={state.order_a}
        onLock={(order) => doApi("/beypong/lock-order", { player_id: selected.player_a_id, order })}
        busy={busy}
      />
      <OrderPicker
        label="B"
        side="b"
        allBeys={allB}
        selectedIds={state.selected_b}
        alreadyLocked={state.order_b_locked}
        peek={peekB}
        setPeek={setPeekB}
        lockedOrder={state.order_b}
        onLock={(order) => doApi("/beypong/lock-order", { player_id: selected.player_b_id, order })}
        busy={busy}
      />
      <div className="text-[10px] font-mono text-white/40">
        Only the referee sees each player's locked order. Both orders are revealed automatically after both players lock in.
      </div>
    </div>
  );
}

function OrderPicker({ label, side, allBeys, selectedIds, alreadyLocked, peek, setPeek, lockedOrder, onLock, busy }) {
  const options = allBeys.filter((b) => selectedIds.includes(b.id));
  const nameOf = (id) => options.find((o) => o.id === id)?.label || id.slice(0, 6);
  const [order, setOrder] = useState(selectedIds && selectedIds.length === 3 ? [...selectedIds] : []);
  const [step, setStep] = useState(alreadyLocked ? 3 : (order.length || 0));
  const pushNext = (id) => {
    if (order.includes(id)) return;
    if (order.length >= 3) return;
    const next = [...order, id];
    setOrder(next);
    setStep(next.length);
  };
  const reset = () => { setOrder([]); setStep(0); };

  if (alreadyLocked) {
    return (
      <div className="border border-[#E2FF00]/40 bg-[#E2FF00]/5 p-3" data-testid={`beypong-order-${side}-locked`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#E2FF00] flex items-center gap-1.5">
            <Lock size={11} /> Player {label} — {t.beyPongOrderLocked}
          </span>
          <button
            onClick={() => setPeek(!peek)}
            data-testid={`beypong-peek-${side}`}
            className="text-[10px] uppercase font-mono text-white/50 hover:text-white flex items-center gap-1"
          >
            {peek ? <><Eye size={11} /> Hide</> : <><EyeSlash size={11} /> Peek</>}
          </button>
        </div>
        {peek && (
          <div className="mt-2 flex gap-1.5 text-[11px] font-mono">
            {(lockedOrder || []).map((id, i) => (
              <span key={i} className="px-2 py-0.5 border border-[#E2FF00]/50 text-[#E2FF00]" data-testid={`beypong-peek-${side}-${i}`}>
                {i + 1}. {nameOf(id)}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-white/10 p-3" data-testid={`beypong-order-${side}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 mb-2 flex items-center gap-2">
        <LockOpen size={11} /> Player {label} — set order 1 → 2 → 3
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {options.map((o) => {
          const idx = order.indexOf(o.id);
          const picked = idx >= 0;
          return (
            <button
              key={o.id}
              disabled={busy || picked}
              onClick={() => pushNext(o.id)}
              data-testid={`beypong-order-btn-${side}-${o.id}`}
              className={`text-left px-2 py-1.5 text-[11px] border font-mono ${picked ? "border-[#E2FF00] bg-[#E2FF00]/10 text-[#E2FF00]" : "border-white/15 text-white/70 hover:border-white/30"}`}
            >
              {picked ? `${idx + 1}. ` : ""}{o.label}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono text-white/40">{order.length}/3 ordered</span>
        <div className="flex gap-2">
          <button
            onClick={reset}
            disabled={busy || order.length === 0}
            className="px-2 py-1 border border-white/20 text-white/60 text-[10px] uppercase disabled:opacity-30"
            data-testid={`beypong-order-reset-${side}`}
          >
            Reset
          </button>
          <button
            disabled={busy || order.length !== 3}
            onClick={() => onLock(order)}
            data-testid={`beypong-lock-${side}`}
            className="px-3 py-1.5 bg-[#FF0055] text-white text-[10px] uppercase font-bold tracking-[0.25em] disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            <Lock size={11} /> {t.beyPongLockOrder}
          </button>
        </div>
      </div>
    </div>
  );
}

function SetEnd({ state, selected, doApi, busy }) {
  return (
    <div className="border border-[#E2FF00]/40 bg-[#E2FF00]/5 p-4 space-y-2" data-testid="beypong-phase-setend">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E2FF00] font-mono">
        {t.beyPongSetEnd}
      </div>
      <div className="text-xs font-mono text-white/60">
        Sets: {state.set_wins_a}:{state.set_wins_b} · Both players must now select a new hidden order.
      </div>
      <button
        onClick={() => doApi("/beypong/select-three", { player_id: selected.player_a_id, bey_ids: state.selected_a })}
        disabled={busy}
        className="px-3 py-1.5 bg-[#00E5FF] text-black text-[10px] uppercase font-bold tracking-[0.25em]"
        data-testid="beypong-new-set-continue"
      >
        Continue → new order
      </button>
    </div>
  );
}

function LiveRound({ state, selected, componentsById, allA, allB, burstPriority, setBurstPriority, doApi, busy }) {
  const nameOf = (id, list) => list.find((o) => o.id === id)?.label || (id ? id.slice(0, 6) : "—");
  const currentBeyA = state.order_a?.[state.round_in_cycle] || null;
  const currentBeyB = state.order_b?.[state.round_in_cycle] || null;
  const scoreFor = (side, key) => {
    const finish_type = key;
    return doApi("/beypong/finish", {
      player_id: side === "a" ? selected.player_a_id : selected.player_b_id,
      finish_type,
      burst_priority: burstPriority && finish_type === "burst",
    }).then(() => setBurstPriority(false));
  };

  return (
    <div className="space-y-3" data-testid="beypong-phase-live">
      {state.last_event === "replay" && (
        <div className="border border-[#FF9F1C]/40 bg-[#FF9F1C]/10 px-3 py-2 text-[11px] font-mono uppercase text-[#FF9F1C]" data-testid="beypong-replay-hint">
          Zetek / launch error — replay this round with the same Beys.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <ActiveBeyChip label="A" beyLabel={nameOf(currentBeyA, allA)} accent="#00E5FF" />
        <ActiveBeyChip label="B" beyLabel={nameOf(currentBeyB, allB)} accent="#FF0055" />
      </div>

      <div className="flex items-center gap-3">
        <label className="text-[10px] font-mono uppercase text-white/60 inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={burstPriority}
            onChange={(e) => setBurstPriority(e.target.checked)}
            data-testid="beypong-burst-priority-toggle"
          />
          {t.beyPongBurstPriority}
        </label>
        <span className="text-[9px] font-mono text-white/30">(overrides Zetek if the next finish is Burst)</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SideFinishColumn label="A" side="a" scoreFor={scoreFor} busy={busy} accent="#00E5FF" />
        <SideFinishColumn label="B" side="b" scoreFor={scoreFor} busy={busy} accent="#FF0055" />
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
        <button
          disabled={busy}
          onClick={() => doApi("/beypong/draw", {})}
          data-testid="beypong-draw"
          className="px-3 py-2 border border-white/20 text-white/70 hover:border-white/40 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-30"
        >
          {t.beyPongDraw}
        </button>
        <button
          disabled={busy}
          onClick={() => doApi("/beypong/launch-error", { player_id: selected.player_a_id })}
          data-testid="beypong-launcherr-a"
          className="px-3 py-2 border border-[#FF9F1C]/40 text-[#FF9F1C] hover:bg-[#FF9F1C]/10 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-30"
        >
          A: {t.beyPongLaunchError}
        </button>
        <button
          disabled={busy}
          onClick={() => doApi("/beypong/launch-error", { player_id: selected.player_b_id })}
          data-testid="beypong-launcherr-b"
          className="px-3 py-2 border border-[#FF9F1C]/40 text-[#FF9F1C] hover:bg-[#FF9F1C]/10 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-30"
        >
          B: {t.beyPongLaunchError}
        </button>
      </div>
    </div>
  );
}

function SideFinishColumn({ label, side, scoreFor, busy, accent }) {
  return (
    <div className="space-y-1.5" data-testid={`beypong-finish-col-${side}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: accent }}>Player {label} scores</div>
      {FINISH_BTNS.map((f) => (
        <button
          key={f.key}
          disabled={busy}
          onClick={() => scoreFor(side, f.key)}
          data-testid={`beypong-finish-${side}-${f.key}`}
          className="w-full text-left px-3 py-2 border text-[11px] uppercase tracking-[0.15em] font-bold hover:bg-white/5 disabled:opacity-30"
          style={{ borderColor: `${f.accent}66`, color: f.accent }}
        >
          {f.label} <span className="text-white/60 font-mono">+{f.pts}</span>
        </button>
      ))}
    </div>
  );
}

function ActiveBeyChip({ label, beyLabel, accent }) {
  return (
    <div className="border p-2 flex items-center gap-2" style={{ borderColor: `${accent}55`, background: `${accent}0d` }}>
      <span className="text-[9px] font-mono uppercase tracking-[0.25em]" style={{ color: accent }}>Player {label}</span>
      <span className="font-display font-black italic uppercase text-sm truncate">{beyLabel}</span>
    </div>
  );
}

function nameFor(componentsById, bey) {
  if (!bey) return "Bey";
  const primaryId = bey.blade || bey.over_blade;
  const c = primaryId ? componentsById?.[primaryId] : null;
  if (c?.name) return c.name;
  return primaryId ? primaryId.slice(0, 6) : "Bey";
}
